import { Mail, Phone } from 'lucide-react'

import { cn } from '../../utils/cn'
import { BrokerActionButton } from './BrokerActionButton'
import { Tooltip, TooltipContent, TooltipTrigger } from './tooltip'

interface BrokerContactActionsProps {
  name: string
  /** Display form, e.g. "(404) 555-0182". */
  phone: string
  email: string
  /** Dialable number for the `tel:` link; defaults to stripping the display phone. */
  phoneE164?: string
  /** Icon-only call button, for widths where the written-out number doesn't fit. */
  hidePhoneNumber?: boolean
  /** Trailing action in the same row, e.g. the note button. */
  action?: React.ReactNode
  className?: string
}

/** Call (with the number shown unless hidden), email and an optional trailing action for a matched broker. */
export function BrokerContactActions({
  name,
  phone,
  email,
  phoneE164,
  hidePhoneNumber,
  action,
  className,
}: BrokerContactActionsProps) {
  const firstName = name.split(' ')[0]

  return (
    <div className={cn('flex shrink-0 items-center gap-1', className)}>
      <Tooltip>
        <TooltipTrigger asChild>
          <BrokerActionButton
            size={hidePhoneNumber ? 'icon-sm' : 'sm'}
            className={hidePhoneNumber ? undefined : 'has-[>svg]:pr-2.5'}
            href={`tel:${phoneE164 || phone.replace(/[^0-9+]/g, '')}`}
            aria-label={hidePhoneNumber ? `Call ${firstName}` : undefined}
          >
            <Phone />
            {!hidePhoneNumber && phone}
          </BrokerActionButton>
        </TooltipTrigger>
        <TooltipContent>Call {firstName}</TooltipContent>
      </Tooltip>
      <Tooltip>
        <TooltipTrigger asChild>
          <BrokerActionButton href={`mailto:${email}`} aria-label={`Email ${firstName}`}>
            <Mail />
          </BrokerActionButton>
        </TooltipTrigger>
        <TooltipContent>Email {firstName}</TooltipContent>
      </Tooltip>
      {action}
    </div>
  )
}
